import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2, ShoppingBag, Clock } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { useCart, formatEUR } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 8;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let timer;

    const poll = async () => {
      attempts += 1;
      try {
        const res = await fetch(`${API}/payments/status/${sessionId}`);
        const data = await res.json();
        if (!res.ok) throw new Error();
        setOrder(data);
        if (data.payment_status === "paid") {
          setState("paid");
          if (!cleared.current) {
            cleared.current = true;
            clear();
          }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
        if (attempts >= MAX_POLLS) {
          setState("pending");
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch {
        if (attempts >= MAX_POLLS) setState("error");
        else timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clear]);

  const config = {
    checking: { icon: Loader2, spin: true, title: "Confirming your payment…", sub: "Just a moment while we check with our secure payment partner." },
    paid: { icon: CheckCircle2, title: "Thank you — your order is confirmed.", sub: "A receipt is on its way to your email. Your clan merchandise will be prepared and shipped shortly." },
    pending: { icon: Clock, title: "Payment is processing.", sub: "This can take a moment. We'll email your receipt as soon as it clears." },
    error: { icon: XCircle, title: "We couldn't confirm this payment.", sub: "If you were charged, please get in touch and we'll sort it out right away." },
  }[state];
  const Icon = config.icon;

  return (
    <div className="relative min-h-screen bg-clan-bg text-clan-ink">
      <div className="grain-overlay" aria-hidden="true" />
      <Header />
      <main className="mx-auto flex min-h-[70vh] max-w-2xl flex-col items-center justify-center px-6 pt-32 text-center">
        <motion.div
          data-testid={`payment-${state}`}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col items-center"
        >
          <span className={`grid h-16 w-16 place-items-center rounded-md border ${state === "paid" ? "border-clan-accent/60 text-clan-accent" : "border-clan-ink/20 text-clan-muted"}`}>
            <Icon className={`h-8 w-8 ${config.spin ? "animate-spin" : ""}`} />
          </span>
          <h1 className="mt-8 font-serif text-3xl font-light leading-tight text-clan-ink sm:text-4xl">{config.title}</h1>
          <p className="mt-4 max-w-md text-base leading-relaxed text-clan-muted">{config.sub}</p>
          {state === "paid" && order?.amount != null && (
            <p className="mt-4 font-mono text-sm uppercase tracking-widest text-clan-accent">
              Paid · {formatEUR(order.amount)}
            </p>
          )}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/shop"
              data-testid="payment-back-to-shop"
              className="flex items-center gap-2 rounded-md border border-clan-ink/15 bg-clan-ink/[0.04] px-6 py-3 font-mono text-xs uppercase tracking-widest text-clan-ink transition-colors hover:border-clan-accent/60"
            >
              <ShoppingBag className="h-4 w-4" /> Back to the store
            </Link>
            <Link
              to="/"
              className="rounded-md bg-clan-primary px-6 py-3 font-mono text-xs uppercase tracking-widest text-white transition-colors hover:bg-clan-primaryHover"
            >
              Return home
            </Link>
          </div>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="relative min-h-screen bg-clan-bg text-clan-ink">
      <div className="grain-overlay" aria-hidden="true" />
      <Header />
      <main className="mx-auto flex min-h-[70vh] max-w-2xl flex-col items-center justify-center px-6 pt-32 text-center">
        <motion.div
          data-testid="payment-cancel"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col items-center"
        >
          <span className="grid h-16 w-16 place-items-center rounded-md border border-clan-ink/20 text-clan-muted">
            <XCircle className="h-8 w-8" />
          </span>
          <h1 className="mt-8 font-serif text-3xl font-light leading-tight text-clan-ink sm:text-4xl">Checkout cancelled.</h1>
          <p className="mt-4 max-w-md text-base leading-relaxed text-clan-muted">
            No payment was taken. Your basket is still saved — you can pick up where you left off whenever you&apos;re ready.
          </p>
          <Link
            to="/shop"
            data-testid="payment-back-to-shop"
            className="mt-10 flex items-center gap-2 rounded-md bg-clan-primary px-6 py-3 font-mono text-xs uppercase tracking-widest text-white transition-colors hover:bg-clan-primaryHover"
          >
            <ShoppingBag className="h-4 w-4" /> Back to the store
          </Link>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}
